/** 百炼集成配置；复用集成弹窗、表单和密钥输入组件，地域与官方地址明确显示。 */
import {
  BAILIAN_REGIONS,
  BailianBaseUrlSchema,
  type BailianCredentialResponse,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useBailianCredential } from '~/data/queries';
import { bailianApi } from '~/hooks/use-bailian';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { PasswordInput } from '~/ui/PasswordInput';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';
import { ProviderConcurrencyField } from './ProviderConcurrencyField';

function ConfigForm({ initial }: { initial: BailianCredentialResponse }) {
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState(initial.base_url ?? BAILIAN_REGIONS[0].base_url);
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () =>
      bailianApi.upsertKey({
        base_url: baseUrl,
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      }),
    onSuccess: async () => {
      setApiKey('');
      await qc.invalidateQueries({ queryKey: ['integrations', 'bailian'] });
      toast('百炼配置已保存', { tone: 'pos' });
    },
    onError: (e) => setError(e.message),
  });
  const region =
    BAILIAN_REGIONS.find(
      (r) => baseUrl.includes(`${r.id}.maas.`) || ('base_url' in r && baseUrl === r.base_url),
    )?.id ?? 'custom';
  return (
    <>
      <FormRow label="服务地域" hint="API Key 必须与地域一致；工作空间专属地址可在下方填写">
        <Select
          value={region}
          disabled={save.isPending}
          onChange={(e) => {
            const selected = BAILIAN_REGIONS.find((r) => r.id === e.target.value);
            if (selected)
              setBaseUrl(
                'base_url' in selected
                  ? selected.base_url
                  : `https://你的工作空间ID.${selected.id}.maas.aliyuncs.com/compatible-mode/v1`,
              );
          }}
        >
          {BAILIAN_REGIONS.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
          <option value="custom" disabled>
            请填写有效的官方地址
          </option>
        </Select>
      </FormRow>
      <FormRow
        label="Base URL"
        required
        hint="从百炼控制台复制 OpenAI 兼容地址，以 /compatible-mode/v1 结尾"
      >
        <input
          aria-label="百炼 Base URL"
          className="input mono"
          value={baseUrl}
          disabled={save.isPending}
          onChange={(e) => setBaseUrl(e.target.value)}
        />
      </FormRow>
      <FormRow
        label="API Key"
        required={!initial.configured}
        hint={
          initial.configured ? '留空保留当前 Key；输入新 Key 可更换' : '百炼控制台 → API Key 获取'
        }
      >
        <PasswordInput
          aria-label="百炼 API Key"
          className="input mono"
          value={apiKey}
          disabled={save.isPending}
          onChange={(e) => setApiKey(e.target.value)}
          autoComplete="new-password"
        />
      </FormRow>
      <p className="muted">
        Key
        加密存储，明文不会回显。保存和测试连接只验证模型目录，不生成内容。模型与提示词在「大模型配置」中设置。
      </p>
      {error && (
        <div role="alert" style={{ color: 'var(--neg)' }}>
          {error}
        </div>
      )}
      <button
        className="btn btn-accent"
        disabled={save.isPending || (!initial.configured && apiKey.trim().length < 8)}
        onClick={() => {
          const result = BailianBaseUrlSchema.safeParse(baseUrl);
          if (!result.success) {
            setError(result.error.issues[0]?.message ?? 'API 地址无效');
            return;
          }
          setError('');
          save.mutate();
        }}
      >
        {save.isPending ? '保存中…' : '保存配置'}
      </button>
    </>
  );
}
export function BailianConfigDialog({ onClose }: { onClose: () => void }) {
  const q = useBailianCredential();
  const qc = useQueryClient();
  const [removing, setRemoving] = useState(false);
  const test = useMutation({
    mutationFn: bailianApi.testConnection,
    onSuccess: () => toast('百炼 Key 验证通过', { tone: 'pos' }),
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  const remove = useMutation({
    mutationFn: bailianApi.removeKey,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['integrations', 'bailian'] });
      setRemoving(false);
      toast('百炼配置已移除', { tone: 'warn' });
    },
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  return (
    <Dialog
      title="阿里云百炼集成配置"
      subtitle="全局模型服务 · 落地页 AI 审核与脱敏"
      icon="IconBranch"
      width={620}
      onClose={onClose}
      closeOnEsc={!removing}
      footer={
        <button className="btn" onClick={onClose}>
          完成
        </button>
      }
    >
      <div style={{ padding: 18 }}>
        {q.isPending ? (
          <p>加载配置…</p>
        ) : q.isError ? (
          <div role="alert">{q.error.message}</div>
        ) : (
          <>
            <p>
              {q.data.configured ? `已配置 · Key 尾号 ${q.data.key_tail}` : '尚未配置阿里云百炼'}
            </p>
            <ConfigForm key={q.data.updated_at ?? 'unset'} initial={q.data} />
            {q.data.configured && (
              <div className="hstack" style={{ gap: 8, marginTop: 16 }}>
                <button className="btn" disabled={test.isPending} onClick={() => test.mutate()}>
                  {test.isPending ? '测试中…' : '测试连接'}
                </button>
                <button
                  className="btn btn-danger"
                  disabled={remove.isPending}
                  onClick={() => setRemoving(true)}
                >
                  移除配置
                </button>
              </div>
            )}
          </>
        )}
        <ProviderConcurrencyField provider="bailian" label="百炼" />
      </div>
      {removing && (
        <ConfirmDialog
          title="移除百炼配置"
          message="移除后使用百炼的审核和脱敏将无法执行；既有任务产物不受影响。"
          requireTyping="百炼"
          tone="danger"
          onConfirm={() => remove.mutate()}
          onClose={() => setRemoving(false)}
        />
      )}
    </Dialog>
  );
}
